import React from "react";
import ReactDOM from "react-dom";
import ReactMarkdown from "react-markdown";
import { Sparkles, Wrench, Zap, X } from "lucide-react";

const CATEGORY_CONFIG = {
  Feature: {
    icon: Sparkles,
    accentColor: "#6366f1",
    badgeStyle: { background: "#eef2ff", color: "#6366f1" },
    btnStyle: { background: "#6366f1" },
  },
  Bugfix: {
    icon: Wrench,
    accentColor: "#f87171",
    badgeStyle: { background: "#fff1f2", color: "#f87171" },
    btnStyle: { background: "#f87171" },
  },
  Enhancement: {
    icon: Zap,
    accentColor: "#14b8a6",
    badgeStyle: { background: "#f0fdfa", color: "#0d9488" },
    btnStyle: { background: "#14b8a6" },
  },
};

const DEFAULT_BTN_STYLE = { background: "#6366f1" };

// Ensures "###heading" becomes "### heading" — CommonMark requires a space after #
function normalizeMarkdown(text) {
  return text.replace(/^(#{1,6})([^\s#])/gm, "$1 $2");
}

function parseTitle(releaseNote) {
  const match = releaseNote.match(/^(Feature|Bugfix|Enhancement):\s*(.+)/i);
  if (match) {
    const key =
      match[1].charAt(0).toUpperCase() + match[1].slice(1).toLowerCase();
    return { category: key, title: match[2].trim() };
  }
  return { category: null, title: releaseNote };
}

const mdComponents = {
  h1: ({ children }) => (
    <h1 className="text-base font-bold text-slate-800 mb-2">{children}</h1>
  ),
  h2: ({ children }) => (
    <h2 className="text-sm font-bold text-slate-800 mb-1.5">{children}</h2>
  ),
  h3: ({ children }) => (
    <h3 className="text-sm font-semibold text-slate-700 mb-1">{children}</h3>
  ),
  p: ({ children }) => (
    <p className="text-sm text-slate-600 mb-2 leading-relaxed">{children}</p>
  ),
  ul: ({ children }) => (
    <ul className="list-disc list-inside text-sm text-slate-600 mb-2 space-y-1">
      {children}
    </ul>
  ),
  ol: ({ children }) => (
    <ol className="list-decimal list-inside text-sm text-slate-600 mb-2 space-y-1">
      {children}
    </ol>
  ),
  li: ({ children }) => (
    <li className="text-sm text-slate-600">{children}</li>
  ),
  strong: ({ children }) => (
    <strong className="font-semibold text-slate-800">{children}</strong>
  ),
  code: ({ children }) => (
    <code className="bg-slate-100 rounded px-1 text-xs font-mono">
      {children}
    </code>
  ),
};

const UpdateBanner = ({
  isVisible = false,
  releaseNote = "",
  description = "",
  newVersion = "",
  onRefresh = () => window.location.reload(),
  onClose = () => {},
}) => {
  if (!isVisible) {
    return null;
  }

  const { category, title } = parseTitle(releaseNote);
  const config = category ? CATEGORY_CONFIG[category] : null;
  const CategoryIcon = config ? config.icon : null;

  const handleRefresh = () => {
    onRefresh();
    window.location.reload();
  };

  return ReactDOM.createPortal(
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[9999] p-4 animate-in fade-in duration-300">
      <div
        className="bg-white rounded-2xl shadow-2xl flex flex-col overflow-hidden animate-in fade-in slide-in-from-bottom-4 duration-300"
        style={{ width: "480px", maxWidth: "100%", maxHeight: "600px" }}
      >
        {/* Colored accent bar */}
        <div style={{ height: "5px", background: config ? config.accentColor : "#6366f1" }} />

        <div className="p-6 flex flex-col gap-4 overflow-hidden">
          {/* Badge + Version + Close */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              {config && (
                <span
                  className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold"
                  style={config.badgeStyle}
                >
                  <CategoryIcon size={12} />
                  {category}
                </span>
              )}
              {newVersion && (
                <span
                  className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold"
                  style={{ background: "#f1f5f9", color: "#64748b" }}
                >
                  v{newVersion}
                </span>
              )}
            </div>
            <button
              onClick={onClose}
              className="text-slate-400 hover:text-slate-600 transition-colors"
            >
              <X size={18} />
            </button>
          </div>

          {/* Title + Subtitle */}
          <div>
            <h2 className="text-xl font-bold text-slate-800 leading-snug">
              {title || "Update Available"}
            </h2>
            <p className="text-sm text-slate-400 mt-1">
              A new version has been deployed.
            </p>
          </div>

          {/* Divider */}
          <div className="border-t border-slate-100" />

          {/* Scrollable description */}
          {description && (
            <div className="overflow-y-auto" style={{ maxHeight: "200px" }}>
              <ReactMarkdown components={mdComponents}>
                {normalizeMarkdown(description)}
              </ReactMarkdown>
            </div>
          )}

          {/* Divider */}
          <div className="border-t border-slate-100" />

          {/* Buttons */}
          <div className="flex gap-3">
            <button
              onClick={onClose}
              className="flex-1 border border-slate-200 text-slate-600 py-2.5 rounded-xl text-sm font-semibold hover:bg-slate-50 active:scale-95 transition-all"
            >
              Later
            </button>
            <button
              onClick={handleRefresh}
              className="flex-1 py-2.5 rounded-xl text-sm font-bold text-white active:scale-95 transition-all"
              style={config ? config.btnStyle : DEFAULT_BTN_STYLE}
            >
              Refresh Now
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default UpdateBanner;
